/* eslint-disable */
import { useState, useRef } from "react";
import { LOGO_B64, generateId, C, styles } from "../ui/tokens";
import { SectionHeader, Callout } from "../ui/primitives";

// ─── SETTINGS TAB ────────────────────────────────────────────────────────────
function SettingsTab({ settings, setSettings }) {
  const logoInputRef = useRef();
  const [teamExpanded, setTeamExpanded] = useState(true);
  const [newMember, setNewMember] = useState({ name: "", email: "" });
  const [editingIdx, setEditingIdx] = useState(null);
  const [editMember, setEditMember] = useState({ name: "", email: "" });

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSettings(s => ({ ...s, logoDataUrl: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const clearLogo = () => setSettings(s => ({ ...s, logoDataUrl: null }));

  const addMember = () => {
    if (!newMember.name.trim()) return;
    setSettings(s => ({ ...s, teamMembers: [...(s.teamMembers || []), { ...newMember, id: generateId() }] }));
    setNewMember({ name: "", email: "" });
  };

  const removeMember = (id) =>
    setSettings(s => ({ ...s, teamMembers: s.teamMembers.filter(m => m.id !== id) }));

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditMember({ ...settings.teamMembers[idx] });
  };

  const saveEdit = () => {
    setSettings(s => ({
      ...s,
      teamMembers: s.teamMembers.map((m, i) => i === editingIdx ? { ...editMember } : m),
    }));
    setEditingIdx(null);
  };

  return (
    <div>
      <div style={styles.card}>
        <SectionHeader icon="⚙️" title="Settings" subtitle="Application preferences for 204Plans" />

        {/* Logo */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <span>🖼️</span> Logo
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <div style={{ width: 120, height: 92, background: C.surface, border: `1px solid ${C.borderLight}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              {settings.logoDataUrl
                ? <img src={settings.logoDataUrl} alt="Logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                : <span style={{ fontSize: 12, color: C.textMuted }}>No logo</span>
              }
            </div>
            <div>
              <div style={{ fontSize: 13, color: C.textSec, marginBottom: 10 }}>
                Upload a PNG or JPG logo file. It will be displayed in the sidebar and on PDF reports.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={styles.btnPrimary} onClick={() => logoInputRef.current.click()}>
                  ⬆ Upload Logo
                </button>
                {settings.logoDataUrl && (
                  <button style={styles.btnDanger} onClick={clearLogo}>Remove</button>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoUpload} />
            </div>
          </div>
        </div>

        {/* Team Members */}
        <div style={{ borderTop: `1px solid ${C.borderLight}`, paddingTop: 24 }}>
          <div
            style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: teamExpanded ? 16 : 0, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
            onClick={() => setTeamExpanded(v => !v)}
          >
            <span>👤</span>
            Team Members
            <span style={{ fontSize: 12, fontWeight: 500, color: C.textMuted, marginLeft: 6 }}>
              ({settings.teamMembers?.length || 0})
            </span>
            <span style={{ marginLeft: "auto", fontSize: 18, color: C.textSec, display: "inline-block", transform: teamExpanded ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
          </div>

          {teamExpanded && (
            <>
              {(settings.teamMembers || []).length === 0 && (
                <Callout type="blue">No team members added yet.</Callout>
              )}
              {(settings.teamMembers || []).map((m, i) => (
                <div key={m.id} style={{ background: C.surface, borderRadius: 10, padding: "12px 16px", marginBottom: 10, border: `1px solid ${C.borderLight}` }}>
                  {editingIdx === i ? (
                    <div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
                        <div>
                          <label style={styles.label}>Name</label>
                          <input style={styles.input} value={editMember.name} onChange={e => setEditMember(em => ({ ...em, name: e.target.value }))} />
                        </div>
                        <div>
                          <label style={styles.label}>Email</label>
                          <input style={styles.input} type="email" value={editMember.email} onChange={e => setEditMember(em => ({ ...em, email: e.target.value }))} />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={styles.btnPrimary} onClick={saveEdit}>Save</button>
                        <button style={styles.btnSecondary} onClick={() => setEditingIdx(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                        {m.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{m.name}</div>
                        <div style={{ fontSize: 12, color: C.textSec }}>{m.email || <span style={{ color: C.textMuted }}>No email</span>}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...styles.btnSecondary, padding: "5px 12px", fontSize: 12 }} onClick={() => startEdit(i)}>Edit</button>
                        <button style={{ ...styles.btnDanger, padding: "5px 12px", fontSize: 12 }} onClick={() => removeMember(m.id)}>✕</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Add new */}
              <div style={{ background: "#f0f4fa", borderRadius: 10, padding: 16, marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textSec, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Add Team Member</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={styles.label}>Name</label>
                    <input style={styles.input} value={newMember.name} placeholder="Full name"
                      onChange={e => setNewMember(m => ({ ...m, name: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && addMember()}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Email</label>
                    <input style={styles.input} type="email" value={newMember.email} placeholder="email@example.com"
                      onChange={e => setNewMember(m => ({ ...m, email: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && addMember()}
                    />
                  </div>
                </div>
                <button style={styles.btnPrimary} onClick={addMember}>+ Add Member</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export { SettingsTab };
